import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Mail, Phone, MapPin, ChevronDown, Sparkles, Brain, TrendingUp, Scale, Users, Cog, Zap, Menu, X, Target, BarChart3, Cpu, Globe, Handshake } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import CookieBanner from './components/CookieBanner';
import LanguageSwitcher from './components/LanguageSwitcher';
import SEO from './components/SEO';
import './App.css';

// Navigation Component
function Navigation() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navItems = [
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.packages'), href: '#packages' },
    { name: t('nav.technologies'), href: '#technologies' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.process'), href: '#process' },
    { name: t('nav.contact'), href: '#contact' }
  ];
  
  const scrollToSection = (e, href) => {
    e.preventDefault();
    // If href is just '#' or empty, scroll to top
    if (href === '#' || href === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      return;
    }
    // Otherwise, scroll to the specified element
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };
  
  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-md border-b border-gray-800 shadow-lg' 
          : 'bg-black/60 backdrop-blur-sm'
      }`}
      style={{ insetInlineStart: 0, insetInlineEnd: 0 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <motion.a
            href="#"
            className="text-2xl font-bold text-white"
            whileHover={{ scale: 1.05 }}
            onClick={(e) => scrollToSection(e, '#')}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              Polyus
            </span>{' '}
            <span>Plus</span>
          </motion.a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item, index) => (
              <motion.a
                key={index}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="text-gray-300 hover:text-white transition-colors text-sm font-medium relative group"
                whileHover={{ y: -2 }}
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-full transition-all duration-300" />
              </motion.a>
            ))}
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* CTA Button */}
            <motion.a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all"
            >
              {t('nav.cta')}
            </motion.a>
          </div>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-black/95 backdrop-blur-md border-t border-gray-800"
        >
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="block text-gray-300 hover:text-white transition-colors text-base font-medium py-2"
              >
                {item.name}
              </a>
            ))}
            
            {/* Mobile Language Switcher */}
            <LanguageSwitcher isMobile={true} />
            
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="block bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-full text-center font-semibold"
            >
              {t('nav.cta')}
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}

// Hero Section
function HeroSection() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  return (
    <motion.section 
      style={{ opacity }}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <video 
        autoPlay 
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source 
          src="/hero-background.mp4" 
          type="video/mp4" 
        />
        Your browser does not support the video tag.
      </video>
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 z-10" />
      
      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-center"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 text-white leading-tight" data-testid="hero-title">
            <span className="block">{t('hero.title1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">{t('hero.title2')}</span></span>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-4 max-w-4xl mx-auto font-light" data-testid="hero-subtitle">
            {t('hero.subtitle')}
          </p>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
            {t('hero.description')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap mb-16">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('packages').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-10 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-blue-500/50 transition-all"
              data-testid="hero-cta-button"
            >
              {t('hero.ctaPrimary')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-all"
            >
              {t('hero.ctaSecondary')}
            </motion.button>
          </div>
          
          {/* Scroll Indicator - centered below buttons */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="flex justify-center items-center"
          >
            <ChevronDown className="w-10 h-10 md:w-12 md:h-12 text-white/80 hover:text-white transition-colors cursor-pointer" 
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
            />
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
}

// About Section
function AboutSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const benefitIcons = [
    <Target className="w-8 h-8" />,
    <BarChart3 className="w-8 h-8" />,
    <Cpu className="w-8 h-8" />,
    <Globe className="w-8 h-8" />,
    <Handshake className="w-8 h-8" />
  ];
  
  const benefits = t('about.benefits', { returnObjects: true }).map((benefit, index) => ({
    icon: benefitIcons[index],
    title: benefit.title,
    description: benefit.description
  }));
  
  return (
    <section ref={ref} id="about" className="py-24 px-4 bg-black" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        {/* Main Title and Description */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-8 leading-tight" data-testid="about-title">
            {t('about.title')}
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 leading-relaxed">
              {t('about.paragraph1')}
            </p>
            
            <p className="text-base sm:text-lg md:text-xl text-gray-400 leading-relaxed">
              {t('about.paragraph2')}
            </p>
          </div>
        </motion.div>
        
        {/* Benefits Title */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">{t('about.whyChoose')}</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 mx-auto mt-4"></div>
        </motion.div>
        
        {/* Benefits Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 p-6 rounded-2xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300 group"
            >
              <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                {benefit.icon}
              </div>
              <h4 className="text-lg md:text-xl font-bold text-white mb-3">{benefit.title}</h4>
              <p className="text-gray-400 leading-relaxed text-sm md:text-base">{benefit.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Packages Section
function PackagesSection({ onPackageSelect }) {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isRTL = i18n.language === 'ar';
  
  const packages = t('packages.items', { returnObjects: true });
  
  const handlePackageClick = (packageName) => {
    onPackageSelect(packageName);
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
    
    // Google Analytics event tracking
    if (window.gtag) {
      window.gtag('event', 'select_package', {
        'package_name': packageName,
        'action': 'click_learn_more'
      });
    }
    
    // Facebook Pixel event tracking - ViewContent
    if (window.fbq) {
      window.fbq('track', 'ViewContent', {
        content_name: packageName,
        content_category: 'Package',
        content_type: 'product'
      });
    }
  };
  
  return (
    <section ref={ref} id="packages" className="py-20 px-4 bg-gradient-to-b from-black to-gray-900" data-testid="packages-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white" data-testid="packages-title">
            {t('packages.title')}
          </h2>
          <p className="text-gray-400 text-base sm:text-lg md:text-xl">{t('packages.subtitle')}</p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative bg-gradient-to-br ${
                pkg.popular 
                  ? 'from-blue-900/50 to-cyan-900/50 border-2 border-blue-500' 
                  : 'from-gray-800/50 to-gray-900/50 border border-gray-700'
              } backdrop-blur-sm rounded-2xl p-6 transition-all duration-300 cursor-pointer group flex flex-col h-full hover:border-blue-400`}
              data-testid={`package-card-${index}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  {t('packages.popular')}
                </div>
              )}
              
              <div className="text-3xl sm:text-4xl mb-3 text-left rtl:text-right">{pkg.icon}</div>
              <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white text-left rtl:text-right">{pkg.name}</h3>
              <p className="text-xs sm:text-sm text-gray-400 mb-3 text-left rtl:text-right">{pkg.tagline}</p>
              <div className="text-xl sm:text-2xl font-bold text-white mb-2 break-words text-left rtl:text-right">{pkg.price}</div>
              <p className="text-xs sm:text-sm text-gray-400 mb-4 text-left rtl:text-right">{pkg.description}</p>
              
              <div className="space-y-2 mb-4 flex-grow">
                {pkg.features.map((feature, i) => (
                  <div key={i} className={`flex items-start text-sm text-gray-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className={`text-blue-400 ${isRTL ? 'ms-2' : 'me-2'}`}>✓</span>
                    <span className={isRTL ? 'text-right' : 'text-left'}>{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-auto">
                <div className="mt-6 pt-4 border-t border-gray-700 mb-6">
                  <p className="text-xs text-gray-400 mb-3 text-left rtl:text-right">{t('packages.result')}:</p>
                  <p className="text-sm text-white font-medium text-left rtl:text-right">{pkg.result}</p>
                </div>
                
                <button 
                  onClick={() => handlePackageClick(pkg.name)}
                  className={`w-full py-3 rounded-full font-semibold transition-all duration-300 ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:shadow-xl hover:shadow-blue-500/60 hover:scale-105 hover:from-blue-400 hover:to-cyan-400'
                      : 'bg-white/10 text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-cyan-500 hover:shadow-xl hover:shadow-blue-500/60 hover:scale-105'
                  }`}
                  data-testid={`package-learn-more-${index}`}
                >
                  {t('packages.learnMore')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Technologies Section
function TechnologiesSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const techIcons = [
    <Brain className="w-12 h-12" />,
    <Cog className="w-12 h-12" />,
    <TrendingUp className="w-12 h-12" />,
    <Sparkles className="w-12 h-12" />
  ];
  
  const technologies = t('technologies.items', { returnObjects: true }).map((tech, index) => ({
    ...tech,
    icon: techIcons[index]
  }));
  
  return (
    <section ref={ref} id="technologies" className="py-20 px-4 bg-black" data-testid="technologies-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">{t('technologies.title')}</h2>
          <p className="text-gray-400 text-base sm:text-lg md:text-xl">{t('technologies.subtitle')}</p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {technologies.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-blue-500 transition-all group"
            >
              <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform">
                {tech.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{tech.title}</h3>
              <p className="text-gray-400 text-sm mb-4">{tech.description}</p>
              <div className="flex flex-wrap gap-2">
                {tech.keywords.map((keyword, i) => (
                  <span key={i} className="text-xs bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full">
                    {keyword}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const serviceIcons = [
    <TrendingUp className="w-10 h-10" />,
    <Zap className="w-10 h-10" />,
    <BarChart3 className="w-10 h-10" />,
    <Scale className="w-10 h-10" />,
    <Users className="w-10 h-10" />,
    <Cog className="w-10 h-10" />,
    <Brain className="w-10 h-10" />
  ];
  
  const services = t('services.items', { returnObjects: true }).map((service, index) => ({
    ...service,
    icon: serviceIcons[index]
  }));
  
  return (
    <section ref={ref} id="services" className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">{t('services.title')}</h2>
          <p className="text-gray-400 text-base sm:text-lg md:text-xl">{t('services.subtitle')}</p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ scale: 1.03 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-blue-500 transition-all group"
              data-testid={`service-card-${index}`}
            >
              <div className="text-blue-400 mb-4 group-hover:text-cyan-400 transition-colors">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3 text-white">{service.title}</h3>
              <p className="text-gray-400 text-sm">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Why Us Section
function WhyUsSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const features = t('whyUs.items', { returnObjects: true });
  
  return (
    <section ref={ref} className="py-20 px-4 bg-black" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-testid="why-us-title">{t('whyUs.title')}</h2>
          <p className="text-gray-400 text-base sm:text-lg md:text-xl">{t('whyUs.subtitle')}</p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center p-6 bg-gradient-to-br from-gray-800/30 to-gray-900/30 rounded-2xl border border-gray-800 hover:border-blue-500/50 transition-all duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="mb-4 flex flex-col items-center justify-center gap-1">
                <div className="flex items-center justify-center gap-2">
                  {feature.prefix && (
                    <span className="text-lg sm:text-xl md:text-2xl font-medium text-blue-400/70">
                      {feature.prefix}
                    </span>
                  )}
                  <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                    {feature.number}
                  </span>
                </div>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 text-white">{feature.title}</h3>
              <p className="text-gray-400 text-sm md:text-base leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Process Section
function ProcessSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const steps = t('process.items', { returnObjects: true });
  
  return (
    <section ref={ref} id="process" className="py-20 px-4 bg-gradient-to-b from-black to-gray-900" data-testid="process-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">{t('process.title')}</h2>
          <p className="text-gray-400 text-base sm:text-lg md:text-xl">{t('process.subtitle')}</p>
        </motion.div>
        
        <div className="relative">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 transform -translate-y-1/2" />
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-blue-500/50">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Contact Section
function ContactSection({ selectedPackage }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    company: '', 
    companyDescription: '',
    selectedPackage: selectedPackage || '',
    goals: [],
    message: '',
    agreeToTerms: false,
    honeypot: '', // Bot trap field
    formLoadTime: Date.now() // Timestamp when form was loaded
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const goals = t('contact.goals', { returnObjects: true });
  
  // Update form when package is selected
  useEffect(() => {
    if (selectedPackage) {
      setFormData(prev => ({ ...prev, selectedPackage }));
    }
  }, [selectedPackage]);
  
  const handleGoalChange = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Submit to Formspree
      const response = await axios.post('https://formspree.io/f/mgvgjvpy', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        companyDescription: formData.companyDescription,
        selectedPackage: formData.selectedPackage,
        goals: formData.goals.join(', '),
        message: formData.message,
        _subject: `New Contact Form: ${formData.name} from ${formData.company}`
      }, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.status === 200) {
        setSubmitStatus('success');
        
        // Google Analytics event tracking
        if (window.gtag) {
          window.gtag('event', 'form_submission', {
            'form_name': 'contact_form',
            'form_type': 'lead_generation',
            'selected_package': formData.selectedPackage || 'none',
            'has_goals': formData.goals.length > 0
          });
        }
        
        // Facebook Pixel event tracking - Lead
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Contact Form Submission',
            content_category: formData.selectedPackage || 'No Package',
            value: formData.selectedPackage ? 1 : 0,
            currency: 'EUR'
          });
        }
        
        setFormData({ 
          name: '', 
          email: '', 
          company: '', 
          companyDescription: '',
          selectedPackage: '',
          goals: [],
          message: '',
          agreeToTerms: false,
          honeypot: '',
          formLoadTime: Date.now()
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section ref={ref} id="contact" className="py-20 px-4 bg-black" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white" data-testid="contact-title">{t('contact.title')}</h2>
          <p className="text-gray-400 text-base sm:text-lg">{t('contact.subtitle')}</p>
        </motion.div>
        
        {/* Contact Form - Centered */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="space-y-6"
          data-testid="contact-form"
        >
            {/* Show selected package */}
            {formData.selectedPackage && (
              <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-300 mb-1">{t('contact.selectedPackage')}</p>
                <p className="text-lg font-bold text-white">{formData.selectedPackage}</p>
              </div>
            )}
            
            {/* Show goals checkboxes only when package is NOT selected */}
            {!formData.selectedPackage && (
              <div className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 border border-gray-700 rounded-2xl p-6">
                <h3 className="text-lg sm:text-xl font-semibold text-white mb-6 text-center">
                  {t('contact.goalQuestion')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {goals.map((goal) => (
                    <label
                      key={goal}
                      className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                        formData.goals.includes(goal)
                          ? 'border-blue-500 bg-gradient-to-r from-blue-500/20 to-cyan-500/20'
                          : 'border-gray-700 hover:border-blue-500/50 bg-gray-800/30'
                      }`}
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={formData.goals.includes(goal)}
                          onChange={() => handleGoalChange(goal)}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-300 ${
                          formData.goals.includes(goal)
                            ? 'bg-gradient-to-r from-blue-500 to-cyan-500 border-blue-500'
                            : 'border-gray-600 bg-gray-700'
                        }`}>
                          {formData.goals.includes(goal) && (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className={`font-medium transition-colors ${
                        formData.goals.includes(goal) ? 'text-white' : 'text-gray-300'
                      }`}>
                        {goal}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-sm text-gray-500 text-center mt-4">
                  {t('contact.multipleChoice')}
                </p>
              </div>
            )}
            
            <div>
              <input
                type="text"
                placeholder={t('contact.form.name')}
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-white focus:outline-none transition-colors"
                data-testid="contact-form-name"
              />
            </div>
            
            <div>
              <input
                type="email"
                placeholder={t('contact.form.email')}
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-white focus:outline-none transition-colors"
                data-testid="contact-form-email"
              />
            </div>
            
            <div>
              <input
                type="text"
                placeholder={t('contact.form.company')}
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-white focus:outline-none transition-colors"
                data-testid="contact-form-company"
              />
            </div>
            
            <div>
              <textarea
                placeholder={t('contact.form.companyDescription')}
                required
                rows={3}
                value={formData.companyDescription}
                onChange={(e) => setFormData({ ...formData, companyDescription: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-white focus:outline-none transition-colors resize-none"
                data-testid="contact-form-company-description"
              />
            </div>
            
            <div>
              <textarea
                placeholder={t('contact.form.message')}
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-white focus:outline-none transition-colors resize-none"
                data-testid="contact-form-message"
              />
            </div>
            
            {/* Terms and Conditions Checkbox */}
            <div className="bg-gradient-to-br from-gray-800/30 to-gray-900/30 border border-gray-700 rounded-2xl p-6">
              <label className="flex items-start space-x-3 cursor-pointer group">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    required
                    checked={formData.agreeToTerms}
                    onChange={(e) => setFormData({ ...formData, agreeToTerms: e.target.checked })}
                    className="sr-only"
                    data-testid="contact-form-terms-checkbox"
                  />
                  <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all duration-300 ${
                    formData.agreeToTerms
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 border-blue-500'
                      : 'border-gray-600 bg-gray-700 group-hover:border-gray-500'
                  }`}>
                    {formData.agreeToTerms && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                  {t('contact.form.terms')}{' '}
                  <a 
                    href="/terms-of-service" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    {t('contact.form.termsLink')}
                  </a>
                  {' '}{t('contact.form.and')}{' '}
                  <a 
                    href="/privacy-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    {t('contact.form.privacyLink')}
                  </a>
                  {' '}{t('contact.form.termsNote')}
                </span>
              </label>
            </div>
            
            {/* Honeypot field - hidden from users but visible to bots */}
            <input
              type="text"
              name="website"
              value={formData.honeypot}
              onChange={(e) => setFormData({ ...formData, honeypot: e.target.value })}
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />
            
            <motion.button
              type="submit"
              disabled={isSubmitting || !formData.agreeToTerms}
              whileHover={{ scale: formData.agreeToTerms ? 1.02 : 1 }}
              whileTap={{ scale: formData.agreeToTerms ? 0.98 : 1 }}
              className="w-full bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="contact-form-submit"
            >
              {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
            </motion.button>
            
            {submitStatus === 'success' && (
              <p className="text-green-400 text-center" data-testid="contact-form-success">{t('contact.form.success')}</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-center" data-testid="contact-form-error">{t('contact.form.error')}</p>
            )}
          </motion.form>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  const { t } = useTranslation();
  
  const handleCookieSettings = () => {
    window.dispatchEvent(new CustomEvent('openCookieSettings'));
  };

  return (
    <footer className="bg-black py-12 px-4 border-t border-gray-800" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 mb-8 max-w-5xl mx-auto">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-4">Polyus Plus</h3>
            <p className="text-gray-400 mb-4">
              {t('footer.description')}
            </p>
            <p className="text-gray-500 text-sm">{t('footer.company')}</p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t('footer.navigation')}</h4>
            <div className="space-y-2">
              <a href="#about" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.about')}</a>
              <a href="#packages" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.packages')}</a>
              <a href="#services" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.services')}</a>
              <a href="#contact" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.contact')}</a>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">{t('footer.legal')}</h4>
            <div className="space-y-2">
              <a href="/terms-of-service" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.terms')}</a>
              <a href="/privacy-policy" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.privacy')}</a>
              <a href="/cookie-policy" className="block text-gray-400 hover:text-white transition-colors">{t('footer.links.cookie')}</a>
              <button 
                onClick={handleCookieSettings}
                className="text-left text-gray-400 hover:text-white transition-colors"
              >
                {t('footer.links.cookieSettings')}
              </button>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 pt-8 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 Polyus Plus OÜ. {t('footer.rights')}
          </p>
          <p className="text-gray-500 mt-2 text-xs">
            {t('footer.keywords')}
          </p>
        </div>
      </div>
    </footer>
  );
}

// Main App
function App() {
  const { i18n } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState(null);
  
  const handlePackageSelect = (packageName) => {
    setSelectedPackage(packageName);
    // Scroll to contact section
    setTimeout(() => {
      document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-black text-white" key={i18n.language}>
      <SEO />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <PackagesSection onPackageSelect={handlePackageSelect} selectedPackage={selectedPackage} />
      <TechnologiesSection />
      <ServicesSection />
      <WhyUsSection />
      <ProcessSection />
      <ContactSection selectedPackage={selectedPackage} />
      <Footer />
      <CookieBanner />
    </div>
  );
}

export default App;